import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';

// Pages
import LoginPage from '@/pages/LoginPage';
import HomePage from '@/pages/HomePage';
import PaymentsPage from '@/pages/PaymentsPage';
import PaymentFormPage from '@/pages/PaymentFormPage';
import BudgetsPage from '@/pages/BudgetsPage';
import SettlementsPage from '@/pages/SettlementsPage';
import SettingsPage from '@/pages/SettingsPage';
import GroupSettingsPage from '@/pages/GroupSettingsPage';
import FamilySetupPage from '@/pages/FamilySetupPage';
import NotificationsPage from '@/pages/NotificationsPage';

// Components
import { Layout } from '@/components/Layout';
import { LoadingScreen } from '@/components/LoadingScreen';

function FamilySetupRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, familyId } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // addNew=true の場合はグループ追加モード（既存グループがあっても表示）
  const searchParams = new URLSearchParams(window.location.search);
  const isAddNewMode = searchParams.get('addNew') === 'true';

  // familyId があり、追加モードでなければ家族設定は不要なのでホームへ
  // （招待コードがあっても、既に参加済みならホームへ）
  if (familyId && !isAddNewMode) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, familyId } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 家族がない場合はグループ作成へ
  if (!familyId) {
    return <Navigate to="/family/setup" replace />;
  }

  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // ログイン済みならホームへ
  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Firebase auth state changed:', firebaseUser?.email);
      if (firebaseUser) {
        // Firebase ユーザーを API に登録/取得
        try {
          const token = await firebaseUser.getIdToken();
          console.log('Got Firebase token, calling login API...');
          // /api/auth/login を呼んでユーザーを作成または更新
          const apiBase = import.meta.env.VITE_API_URL || '/api';
          const response = await fetch(`${apiBase}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });

          console.log('Login API response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('Login API response data:', data);
            setUser(
              {
                id: data.id,
                email: data.email,
                displayName: data.displayName,
                photoUrl: data.photoUrl,
              },
              data.familyId || null,
              data.isPremium || false
            );
          } else {
            const errorText = await response.text();
            console.error('Login API error:', response.status, errorText);
            setUser(null, null);
          }
        } catch (error) {
          console.error('Auth error:', error);
          setUser(null, null);
        }
      } else {
        setUser(null, null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setLoading]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <AuthRoute>
              <LoginPage />
            </AuthRoute>
          }
        />
        <Route
          path="/family/setup"
          element={
            <FamilySetupRoute>
              <FamilySetupPage />
            </FamilySetupRoute>
          }
        />
        <Route
          path="/invite"
          element={
            <FamilySetupRoute>
              <FamilySetupPage />
            </FamilySetupRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/new" element={<PaymentFormPage />} />
          <Route path="payments/:id/edit" element={<PaymentFormPage />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="settlements" element={<SettlementsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/group" element={<GroupSettingsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
